// web/src/pages/statements/BulkStatementRunWizard.tsx
//
// I-8 — 4-step "Bulk statement run" wizard (Plan III Task 22).
//
// Step 1: Select accounts (filter inputs)
// Step 2: Choose delivery mode
// Step 3: Preview (count-only — spec sample-render dropped; see deviation flag below)
// Step 4: Confirm + run
//
// Plan IV Task 7: Migrated from manual useState<1|2|3|4> step machine +
// bespoke step indicator + bespoke Back/Next button rows to the canonical
// Wizard archetype. Shared form state (react-hook-form) lives in the parent
// component; each step is a small local component that reads/writes that form.
import { useForm, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Wizard, type WizardStep } from "@/archetypes/Wizard";
import {
  createStatementRun,
  type CreateStatementRunRequest,
} from "@/lib/invoices-payments";

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const Schema = z.object({
  onlyHasOpenBalance: z.boolean(),
  branchIds: z.string(),                   // CSV → number[]
  termsIds: z.string(),                    // CSV → number[]
  deliveryMethodCodes: z.string(),         // CSV → string[]
  deliveryMode: z.enum(["Email", "Print", "Both"]),
  notes: z.string().max(2000).optional(),
});
type FormValues = z.infer<typeof Schema>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const csvToNumbers = (s: string): number[] =>
  s
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean)
    .map(Number)
    .filter((n) => Number.isFinite(n));

const csvToStrings = (s: string): string[] =>
  s
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

// ---------------------------------------------------------------------------
// Step components — shared form state injected as prop
// ---------------------------------------------------------------------------

interface StepProps {
  form: UseFormReturn<FormValues>;
}

function Step1SelectAccounts({ form }: StepProps) {
  return (
    <section aria-label="Step 1: Select accounts" className="space-y-4 max-w-xl">
      <p className="text-sm text-muted-foreground">
        step 1 of 4 — choose which accounts to include in this run.
      </p>
      <div className="flex items-center gap-2">
        <Checkbox
          id="onlyOpen"
          checked={form.watch("onlyHasOpenBalance")}
          onCheckedChange={(checked) =>
            form.setValue("onlyHasOpenBalance", checked === true)
          }
        />
        <Label htmlFor="onlyOpen">Only accounts with open balance</Label>
      </div>
      <div>
        <Label htmlFor="branchIds">Branch IDs (comma-separated, optional)</Label>
        <Input
          id="branchIds"
          placeholder="e.g. 1, 2, 3"
          {...form.register("branchIds")}
        />
      </div>
      <div>
        <Label htmlFor="termsIds">Terms IDs (comma-separated, optional)</Label>
        <Input
          id="termsIds"
          placeholder="e.g. 10, 20"
          {...form.register("termsIds")}
        />
      </div>
      <div>
        <Label htmlFor="deliveryMethodCodes">
          Delivery method codes (comma-separated, optional)
        </Label>
        <Input
          id="deliveryMethodCodes"
          placeholder="e.g. Email"
          {...form.register("deliveryMethodCodes")}
        />
      </div>
    </section>
  );
}

function Step2DeliveryMode({ form }: StepProps) {
  return (
    <section aria-label="Step 2: Delivery mode" className="space-y-4 max-w-xl">
      <p className="text-sm text-muted-foreground">
        step 2 of 4 — choose how statements will be delivered.
      </p>
      <div>
        <Label>Delivery mode</Label>
        <Select
          value={form.watch("deliveryMode")}
          onValueChange={(v) =>
            form.setValue("deliveryMode", v as FormValues["deliveryMode"])
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Email">Email</SelectItem>
            <SelectItem value="Print">Print</SelectItem>
            <SelectItem value="Both">Both (email + print)</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </section>
  );
}

function Step3Preview({ form }: StepProps) {
  return (
    <section aria-label="Step 3: Preview" className="space-y-4 max-w-xl">
      <p className="text-sm text-muted-foreground">
        step 3 of 4 — review before submitting.
      </p>
      <p className="text-sm text-slate-600">
        Preview: filter will be applied at run time. Per-account row preview is
        deferred — confirm to enqueue and watch live progress on the next page.
      </p>
      {/* TODO (Plan III follow-up): per-account preview + sample-render link */}
      <div className="rounded-md border p-3 text-sm">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
          <dt className="text-muted-foreground">Delivery mode</dt>
          <dd>{form.watch("deliveryMode")}</dd>
          <dt className="text-muted-foreground">Open balance only</dt>
          <dd>{form.watch("onlyHasOpenBalance") ? "Yes" : "No"}</dd>
          <dt className="text-muted-foreground">Branch IDs</dt>
          <dd>{form.watch("branchIds") || "(all)"}</dd>
          <dt className="text-muted-foreground">Terms IDs</dt>
          <dd>{form.watch("termsIds") || "(all)"}</dd>
          <dt className="text-muted-foreground">Delivery method codes</dt>
          <dd>{form.watch("deliveryMethodCodes") || "(all)"}</dd>
        </dl>
      </div>
    </section>
  );
}

interface Step4Props extends StepProps {
  isError: boolean;
}

function Step4Confirm({ form, isError }: Step4Props) {
  return (
    <section aria-label="Step 4: Confirm" className="max-w-xl space-y-4">
      <p className="text-sm text-muted-foreground">
        step 4 of 4 — add optional notes and start the run.
      </p>
      <div>
        <Label htmlFor="notes">Notes (optional)</Label>
        <Input
          id="notes"
          placeholder="e.g. Month-end run May 2026"
          {...form.register("notes")}
        />
      </div>
      {isError && (
        <p className="text-sm text-destructive">
          Could not enqueue the run. Please retry.
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function BulkStatementRunWizard() {
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: {
      onlyHasOpenBalance: true,
      branchIds: "",
      termsIds: "",
      deliveryMethodCodes: "",
      deliveryMode: "Email",
      notes: "",
    },
  });

  const createMut = useMutation({
    mutationFn: (req: CreateStatementRunRequest) => createStatementRun(req),
    onSuccess: ({ runId }) => navigate(`/statements/runs/${runId}`),
  });

  function handleFinish() {
    const values = form.getValues();
    createMut.mutate({
      deliveryMode: values.deliveryMode,
      notes: values.notes || undefined,
      accountFilter: {
        onlyHasOpenBalance: values.onlyHasOpenBalance,
        branchIds: csvToNumbers(values.branchIds),
        termsIds: csvToNumbers(values.termsIds),
        deliveryMethodCodes: csvToStrings(values.deliveryMethodCodes),
      },
    });
  }

  const steps: WizardStep[] = [
    {
      id: "select-accounts",
      label: "Select accounts",
      render: () => <Step1SelectAccounts form={form} />,
    },
    {
      id: "delivery-mode",
      label: "Delivery mode",
      render: () => <Step2DeliveryMode form={form} />,
    },
    {
      id: "preview",
      label: "Preview",
      render: () => <Step3Preview form={form} />,
    },
    {
      id: "confirm",
      label: "Confirm",
      render: () => <Step4Confirm form={form} isError={createMut.isError} />,
      // Override the default Finish button on the last step so the action is
      // labelled "Run now" (and reflects the mutation's pending state).
      actions: (ctx) => (
        <>
          <Button variant="outline" disabled={ctx.isFirst} onClick={ctx.goPrev}>
            Back
          </Button>
          <Button onClick={handleFinish} disabled={createMut.isPending}>
            {createMut.isPending ? "Enqueuing..." : "Run now"}
          </Button>
        </>
      ),
    },
  ];

  return (
    <div data-testid="bulk-statement-run-wizard">
      <Wizard title="Bulk Statement Run" steps={steps} onFinish={handleFinish} />
    </div>
  );
}
